<template>
  <b-card no-body>
    <!-- Card header -->
    <b-card-header>
      <!-- Title -->
      <h5 class="h3 mb-0">Members</h5>
    </b-card-header>
    <!-- Card body -->
    <b-card-body>
      <div class="text-center text-lg" v-if="loading">
        <i class="fa fa-spin fa-spinner mr-2"></i>Loading&hellip;
      </div>
      <!-- List group -->
      <b-list-group v-if="!loading" flush class="list my--3">
        <b-list-group-item
          v-for="(member, index) in members"
          :key="member.user.id"
          class="list-group-item px-0"
        >
          <b-row align-v="center">
            <b-col md="2">
              <!-- Avatar -->
              <b-img
                class="img-fluid"
                rounded="circle"
                alt="Image placeholder"
                :src="member.user.avatarUrl"
              />
            </b-col>
            <b-col class="ml--2">
              <h4 class="mb-0">{{ member.user.displayName }}</h4>
              <small class="text-uppercase">{{ member.role }}</small>
              <small class="mx-2 text-muted">
                <i class="fa fa-circle"></i>
              </small>
              <small>Added {{ member.createdAt | moment("from", "now") }}</small>
            </b-col>
            <b-col md="auto">
              <b-button type="button" size="sm" variant="primary">Change</b-button>
              <b-button
                type="button"
                size="sm"
                variant="primary"
                @click="deleteMember(member, index)"
              >
                <span v-if="member.user.id === user.id">Leave</span>
                <span v-else>Remove</span>
              </b-button>
            </b-col>
          </b-row>
        </b-list-group-item>

        <b-list-group-item
          v-for="(invitation, index) in invitations"
          :key="invitation.id"
          class="list-group-item px-0"
        >
          <b-row align-v="center">
            <b-col md="2" class="text-center">
              <!-- Avatar -->
              <i class="fa fa-user-circle fa-2x text-muted"></i>
            </b-col>
            <b-col class="ml--2">
              <h4 class="mb-0">{{ invitation.emailAddress }}</h4>
              <small class="text-uppercase">{{ invitation.role }}</small>
              <small class="mx-2 text-muted">
                <i class="fa fa-circle"></i>
              </small>
              <small>
                <span v-if="$moment(invitation.expiresAt).isAfter()">Expires</span>
                <span v-else>Expired</span>
                {{ invitation.expiresAt | moment("from", "now") }}
              </small>
            </b-col>
            <b-col md="auto">
              <b-button
                type="button"
                size="sm"
                variant="secondary"
                @click="deleteInvitation(invitation, index)"
              >Cancel invitation</b-button>
            </b-col>
          </b-row>
        </b-list-group-item>
      </b-list-group>
    </b-card-body>
    <b-card-footer class="text-right">
      <b-btn v-b-modal="'invitation-create-modal'" variant="default" size="sm">
        <i class="fa fa-user-plus mr-2"></i> Add user
      </b-btn>
    </b-card-footer>

    <invitation-create-form :account-id="accountId" @create-invitation="invitationCreated"></invitation-create-form>
  </b-card>
</template>
<script>
import { Modal, BaseAlert } from "@/components";
import { get } from "dot-prop";
import InvitationCreateForm from "./InvitationCreateForm";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";

export default {
  name: "account-members",
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  components: {
    BaseAlert,
    InvitationCreateForm,
  },
  data() {
    return {
      loading: false,
      members: [],
      invitations: [],
      errorMessage: null,
      invitationModalOpen: false,
    };
  },
  computed: {
    user() {
      return this.$auth.user();
    },
  },
  methods: {
    invitationCreated: function (invitation) {
      this.invitations.push(invitation);
    },
    deleteInvitation: function (invitation, index) {
      swal
        .fire({
          title: "Are you sure?",
          text: `${invitation.emailAddress} won't be able to join your account.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "gray",
          confirmButtonText: "Yes, delete invitation!",
          reverseButtons: true,
          showLoaderOnConfirm: true,
          preConfirm: (login) => {
            return this.axios
              .delete(
                `/v1/accounts/${this.accountId}/invitations/${invitation.id}`
              )
              .then((response) => {
                return response;
              })
              .catch((error) => {
                swal.showValidationMessage(
                  `Request failed: ${error.response.data.message}`
                );
              });
          },
          allowOutsideClick: () => !swal.isLoading(),
        })
        .then((result) => {
          if (result.value) {
            swal.fire("Done!", "The invitation was withdrawn", "success");

            // delete invitation from list
            this.invitations.splice(index, 1);
          }
        });
    },
    deleteMember: function (member, index) {
      let deletingOwnMembership = false;
      if (member.user.id === this.user.id) {
        deletingOwnMembership = true;
      }
      swal
        .fire({
          title: "Are you sure?",
          text: `${
            deletingOwnMembership ? "You" : member.user.displayName
          } will be removed from your account.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "gray",
          confirmButtonText: "Yes, remove!",
          reverseButtons: true,
          showLoaderOnConfirm: true,
          preConfirm: (login) => {
            return this.axios
              .delete(
                `/v1/accounts/${this.accountId}/members/${member.user.id}`
              )
              .then((response) => {
                return response;
              })
              .catch((error) => {
                swal.showValidationMessage(
                  `Request failed: ${error.response.data.message}`
                );
              });
          },
          allowOutsideClick: () => !swal.isLoading(),
        })
        .then((result) => {
          if (result.value) {
            swal.fire("Done!", "The member was removed", "success");

            // delete member from list
            this.members.splice(index, 1);

            if (deletingOwnMembership) {
              // redirect to account select
              this.$router.push("/select-account");
            }
          }
        });
    },
  },
  created() {
    this.loading = true;

    this.axios
      .get(`/v1/accounts/${this.accountId}/members`)
      .then((response) => {
        // this.loading = false;
        if (response.data) {
          this.members = response.data;
        }

        return this.axios.get(
          `/v1/accounts/${this.accountId}/invitations?isAccepted=false`
        );
      })
      .then((response) => {
        this.loading = false;
        if (response.data) {
          this.invitations = response.data;
        }
      })
      .catch((error) => {
        this.loading = false;
        this.errorMessage = "Failed to create account";
        if (get(error, "response.data.message")) {
          this.errorMessage = error.response.data.message;
        }
      });
  },
};
</script>
